import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Form } from 'react-final-form';
import RadioGroupField from 'components/RadioGroupField';
import PencilaltIcon from 'icons/Pencilalt.icon';
import UploadIcon from 'icons/Upload.icon';
import { YesNo } from 'models/Fields';
import { Button } from 'ncoded-component-library';
import ArrowrightIcon from 'icons/Arrowright.icon';
import ResolveHeader from 'components/ResolveHeader';
import { getCreateListingRoute } from 'utils';
import { ImField } from 'components/ImField';
import { LISTING_IM } from 'hooks/useUploadIm';
import DealRoomContext from '../../providers/DealRoom/DealRoom.context';

import './MarketYourListing.styles.scss';
import './MarketYourListing.styles.responsive.scss';

const MarketYourListing: FC = () => {
  const { dealRoom } = useContext(DealRoomContext);

  const { id: dealRoomId } = useParams();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const draftListingOptions = useMemo(
    () => [
      {
        value: YesNo.Yes,
        label: (
          <>
            <UploadIcon />
            <span>
              {t('yesUploadIm')} <p>{t('weWillDraftListing')}</p>
            </span>
          </>
        ),
      },
      {
        value: YesNo.No,
        label: (
          <>
            <PencilaltIcon />
            <span>
              {t('noWillDoItMyself')} <p>{t('useLCW')}</p>
            </span>
          </>
        ),
      },
    ],
    [t],
  );

  const onSubmit = useCallback(
    (values) => {
      if (values.draftListing === YesNo.Yes) {
        navigate('../im-upload-successful', { replace: true });
        return;
      }

      const {
        _id,
        listing: { _id: listingId, type },
      } = dealRoom;

      const lcwLink = getCreateListingRoute(_id, listingId, type);

      navigate(lcwLink, {
        replace: true,
      });
    },
    [dealRoom, navigate],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        [LISTING_IM]: dealRoom?.listingIM,
        draftListing: dealRoom?.listingIM ? YesNo.Yes : null,
      }}
      render={({
        submitting,
        invalid,
        values: { draftListing },
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit} className="market-your-listing">
          <ResolveHeader />

          <main>
            <div>
              <h1>{t('marketYourDealWithListing')}</h1>
              <p className="market-your-listing__description">
                {t('wantUsToDraftListingForYou')}
              </p>

              <RadioGroupField
                styled
                options={draftListingOptions}
                name="draftListing"
              />

              {draftListing === YesNo.Yes && (
                <ImField dealRoomId={dealRoomId} />
              )}

              <div className="market-your-listing__action">
                <span />
                <Button
                  disabled={invalid || submitting}
                  icon={ArrowrightIcon}
                  iconPosition="right"
                  type="submit"
                >
                  {t('next')}
                </Button>
              </div>
            </div>
          </main>
        </form>
      )}
    />
  );
};

export default MarketYourListing;
